export type FlagResolution = {
  base: boolean | undefined;
  override: boolean | undefined;
  effective: boolean;
  source: 'inherit' | 'override' | 'unset';
};

/**
 * Mirrors the server-side `resolveAdvisorFlags` in fetchFeatureFlags.ts but
 * returns structured per-key information so the UI can distinguish:
 *   - Inherit from allAdvisors (override absent)
 *   - Explicit override = true
 *   - Explicit override = false
 */
export function resolveAdvisorFlag(
  config: AppConfigSchema,
  advisorId: string,
  flagKey: string,
): FlagResolution {
  const base = (config.allAdvisors as Record<string, boolean | undefined> | undefined)?.[flagKey];
  const overrides = (config.advisors ?? {})[advisorId] as
    | Record<string, boolean | undefined>
    | undefined;
  const hasOverride = overrides ? Object.prototype.hasOwnProperty.call(overrides, flagKey) : false;
  const override = hasOverride ? overrides?.[flagKey] : undefined;

  if (hasOverride) {
    return {
      base,
      override,
      effective: override === true,
      source: 'override',
    };
  }

  if (base !== undefined) {
    return {
      base,
      override: undefined,
      effective: base === true,
      source: 'inherit',
    };
  }

  return {
    base: undefined,
    override: undefined,
    effective: false,
    source: 'unset',
  };
}

export function resolveAdvisorAllFlags(
  config: AppConfigSchema,
  advisorId: string,
  flagKeys: readonly string[],
): Record<string, FlagResolution> {
  const result: Record<string, FlagResolution> = {};
  for (const key of flagKeys) {
    result[key] = resolveAdvisorFlag(config, advisorId, key);
  }
  return result;
}

/**
 * For the flag-centric view: given a flag key, return the list of advisors
 * that have an explicit override (true or false) for this flag.
 */
export function findAdvisorsWithOverride(
  config: AppConfigSchema,
  flagKey: string,
): Array<{ advisorId: string; value: boolean }> {
  const result: Array<{ advisorId: string; value: boolean }> = [];
  for (const [advisorId, overrides] of Object.entries(config.advisors ?? {})) {
    const record = overrides as Record<string, boolean | undefined> | undefined;
    if (record && Object.prototype.hasOwnProperty.call(record, flagKey)) {
      const value = record[flagKey];
      if (typeof value === 'boolean') {
        result.push({ advisorId, value });
      }
    }
  }
  return result.sort((a, b) => a.advisorId.localeCompare(b.advisorId));
}
